/**
 * EntityMentionChip - Inline entity reference chip
 *
 * Displays entity mentions in coach messages with:
 * - Domain-colored backgrounds
 * - Click handlers for navigation
 * - Tag icon indicator
 *
 * Usage: Parse `[Label](#entity-uri)` in markdown and replace with this chip.
 */

import { Tag } from 'lucide-react'
import type { EntityDomain } from '@ispot/shared'

// ============================================================================
// TYPES
// ============================================================================

interface EntityMentionChipProps {
  entityId: string // URI or ID
  label: string
  domain: EntityDomain
  onClick?: () => void
}

// ============================================================================
// COMPONENT
// ============================================================================

export function EntityMentionChip({ entityId, label, domain, onClick }: EntityMentionChipProps) {
  // Domain colors
  const domainColor = getDomainColor(domain)

  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium transition-opacity hover:opacity-80"
      style={{
        backgroundColor: `${domainColor}15`,
        borderWidth: '1px',
        borderColor: `${domainColor}40`,
        color: domainColor,
      }}
      title={`Click to view ${label}`}
    >
      <Tag className="w-3 h-3" />
      {label}
    </button>
  )
}

// ============================================================================
// HELPERS
// ============================================================================

function getDomainColor(domain: EntityDomain): string {
  switch (domain) {
    case 'market':
      return '#5c8a6b'
    case 'business':
      return '#3d7a8c'
    case 'solution':
      return '#c9a857'
  }
}
